import React, { useEffect, useState } from 'react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import BeneficiaryAvatar from '../../../../../components/BeneficiaryAvatar'
import InfoField from '../../../../../components/InfoField'
import FormatUtils from '../../../../../utils/FormatUtils'
import ButtonLink from '../../../../../components/ButtonLink'
import Icon from '@mdi/react'
import { mdiAccountEdit, mdiPlaylistEdit } from '@mdi/js'
import { useBreakpoints } from '../../../../../components/breakpoints/BreakpointsProvider'
import ModifyPersonalInfo from './ModifyPersonalInfo'
import SuggestService from '../../../../../api/gerep/SuggestService'
import { useHistory } from 'react-router-dom'
import { SECONDARY_PATH } from '../../../../../constant/path'
import { maskNSS, maskTelephone } from '../../../../../utils/authentUtils'
import { SOCIAL_SECURITY_CHOICES } from '../../services/personalInfoConstants'

type PersonalInfoProps = {
	assureConnecte: any
	assurePrincipal: any
	doubleAuthent: string
	user: any
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({ assureConnecte, assurePrincipal, doubleAuthent, user }) => {
	const intl = useIntl()
	const { isMobile, isTabletPortrait } = useBreakpoints()
	const history = useHistory()

	const [openModifyInfo, setOpenModifyInfo] = useState<boolean>(false)
	const [pays, setPays] = useState<Country[]>([])

	useEffect(() => {
		SuggestService.getPays()
			.then((response) => setPays(response))
	}, [])

	return (
		<Stack direction="column" spacing="24px">
			<Typography variant="h2">
				<FormattedMessage id="personalInfo.personalData.title" />
			</Typography>
			<Stack direction="column" spacing="14px" pl="24px" pr="12px">
				<Stack direction="row" spacing="8px" alignItems="center">
					<BeneficiaryAvatar firstname={assureConnecte?.prenom} lastname={assureConnecte?.nom} size="large" type={0} />
					<Typography variant="h3">
						{`${assureConnecte?.prenom} ${assureConnecte?.nom}`}
					</Typography>
				</Stack>
				<Stack direction="column" spacing="14px" alignItems="center">
					<Stack width="100%" direction={isMobile ? 'column' : 'row'} spacing="16px" justifyContent="space-between">
						<InfoField
							label={intl.formatMessage({ id: 'personalInfo.personalData.labels.birth' })}
							value={assureConnecte?.dateNaissance}
						/>
						{!isMobile ?
							<InfoField
								label={intl.formatMessage({ id: 'personalInfo.personalData.labels.ssNumber' })}
								value={FormatUtils.formatNiss(maskNSS(assureConnecte?.matorg))}
							/>
							:
							<InfoField
								label={intl.formatMessage({ id: 'personalInfo.personalData.labels.address' })}
								value={
									<Stack direction="column">
										<Typography variant="body1">{assurePrincipal?.adresse?.adresse}</Typography>
										<Typography variant="body1">{`${assurePrincipal?.adresse?.codpos} ${assurePrincipal?.adresse?.ville}, ${assurePrincipal?.adresse?.libPays}`}</Typography>
									</Stack>
								}
							/>
						}
					</Stack>
					<Stack width="100%" direction={isMobile ? 'column' : 'row'} spacing="16px" justifyContent="space-between">
						{!isMobile && assurePrincipal?.adresse?
							<>
								<InfoField
									label={intl.formatMessage({ id: 'personalInfo.personalData.labels.address' })}
									value={
										<Stack direction="column">
											{assurePrincipal?.adresse?.adresse &&
												<Typography variant="body1">
													{assurePrincipal?.adresse?.adresse}
												</Typography>
											}
											{assurePrincipal?.adresse?.adresse2 &&
												<Typography variant="body1">
													{assurePrincipal?.adresse?.adresse2}
												</Typography>
											}
											{assurePrincipal?.adresse?.adresse3 &&
												<Typography variant="body1">
													{assurePrincipal?.adresse?.adresse3}
												</Typography>
											}
											{assurePrincipal?.adresse?.adresse4 &&
												<Typography variant="body1">
													{assurePrincipal?.adresse?.adresse4}
												</Typography>
											}
											{assurePrincipal?.adresse?.codpos && assurePrincipal?.adresse?.ville && assurePrincipal?.adresse?.libPays &&
												<Typography variant="body1">
													{`${assurePrincipal?.adresse?.codpos} ${assurePrincipal?.adresse?.ville}, ${assurePrincipal?.adresse?.libPays}`}
												</Typography>
											}
										</Stack>
									}
								/>
								<InfoField
									label={intl.formatMessage({ id: 'personalInfo.personalData.labels.phone' })}
									value={maskTelephone(assureConnecte?.telephone)}
								/>
							</> :
							<>
								<InfoField
									label={intl.formatMessage({ id: 'personalInfo.personalData.labels.ssNumber' })}
									value={FormatUtils.formatNiss(maskNSS(assureConnecte?.matorg))}
								/>
								<InfoField
									label={intl.formatMessage({ id: 'personalInfo.personalData.labels.phone' })}
									value={maskTelephone(assureConnecte?.telephone)}
								/>
							</>
						}
					</Stack>
				</Stack>
				<Stack direction={isMobile || isTabletPortrait ? 'column' : 'row'} spacing="14px" alignItems={isMobile || isTabletPortrait ? 'flex-start' : 'center'}>
					<Box display="flex" flex={1}>
						<ButtonLink
							variant="contained"
							startIcon={<Icon size="20px" path={mdiAccountEdit} />}
							onClick={() => setOpenModifyInfo(true)}
							style={{ height: isMobile ? 'auto' : '36px' }}
						>
							<FormattedMessage id="personalInfo.personalData.buttons.update" />
						</ButtonLink>
					</Box>
					{ !user.affiliationExpired &&
						<Box display="flex" flex={1}>
							<ButtonLink variant="contained" startIcon={<Icon size="20px" path={mdiPlaylistEdit} />} onClick={() => {
								history.push({
									pathname: SECONDARY_PATH.CONNEXION_NOEMIE,
									state: { option: SOCIAL_SECURITY_CHOICES.MODIFY }
								})
							}}>
								<FormattedMessage id="personalInfo.personalData.buttons.modify" />
							</ButtonLink>
						</Box>
					}
				</Stack>
			</Stack>
			<Divider />

			<ModifyPersonalInfo pays={pays} open={openModifyInfo} setOpen={setOpenModifyInfo} user={assureConnecte} assurePrincipal={assurePrincipal} doubleAuthent={doubleAuthent} />
		</Stack>
	)
}

export default PersonalInfo
